import React from 'react'
import { Layout, Table, Modal, message } from 'antd'
import moment from 'moment'
import lecturesService from '@services/mediwho-app'
import LectureModal from './LectureModal'
import NotificationModal from '../Notifications/NotificationModal'
import {
    mediwhoAppTableWrapStyle,
    centeredBodyCell,
    centeredHeaderCell,
    MediwhoAppPageHeader,
    renderTruncatedValue,
    renderLinkButton,
    renderActionButtons,
    rtlLayoutStyle,
    confirmDelete
} from '../shared/mediwhoAppStyles'

export default class LecturesPage extends React.Component<any, any> {
    state = {
        data: [],
        total: 0,
        page: 1,
        loading: false,
        modalOpen: false,
        selected: null as any,
        notifModalOpen: false,
        notifSaving: false,
        notifPrefill: null as any,
        pendingLecture: null as any,
        pendingLectureId: null as any
    }

    componentDidMount() {
        this.load(1)
    }

    load = async (page: number) => {
        this.setState({ loading: true })
        try {
            const res = await lecturesService.list({ page })
            const { data, total } = res.data
            this.setState({ data, total, page })
        } catch (e) {
            console.error(e)
        } finally {
            this.setState({ loading: false })
        }
    }

    deleteLecture = (id: number) => {
        confirmDelete('מחיקת הרצאה', 'האם אתה בטוח שברצונך למחוק את ההרצאה?', async () => {
            try {
                await lecturesService.delete(id)
                message.success('ההרצאה נמחקה')
                this.load(this.state.page)
            } catch (e) {
                console.error(e)
                Modal.error({ title: 'שגיאה', content: 'שגיאה במחיקת ההרצאה' })
            }
        })
    }

    openAdd = () => this.setState({ modalOpen: true, selected: null })
    openEdit = (row: any) => this.setState({ modalOpen: true, selected: row })
    closeModal = () => this.setState({ modalOpen: false, selected: null })
    onSaved = async (triggerNotification: boolean, lectureData: { topic: string, description: string }, body: any) => {
        const isNew = !(this.state.selected && this.state.selected.id)

        if (!isNew) {
            try {
                await lecturesService.update(this.state.selected.id, body)
            } catch (e) {
                message.error('שגיאה בשמירת ההרצאה')
                return
            }
        } else if (!triggerNotification) {
            try {
                await lecturesService.create(body)
            } catch (e) {
                message.error('שגיאה בשמירת ההרצאה')
                return
            }
        }

        this.closeModal()
        if (triggerNotification) {
            const prefill = { title: 'עלתה הרצאה חדשה בנושא ' + lectureData.topic + '!', body: lectureData.description ? 'ההרצאה תעסוק ב' + lectureData.description + '.' : '', action: 'open_lectures' }
            this.setState({
                notifModalOpen: true,
                pendingLecture: isNew ? body : null,
                pendingLectureId: isNew ? null : this.state.selected.id,
                notifPrefill: prefill
            })
        } else {
            message.success(isNew ? 'ההרצאה נוצרה' : 'ההרצאה עודכנה')
        }
        this.load(this.state.page)
    }

    handleNotifSubmit = async (payload: any) => {
        this.setState({ notifSaving: true })
        try {
            let lectureId: number | null = null
            if (this.state.pendingLecture) {
                const lectureRes = await lecturesService.create(this.state.pendingLecture)
                lectureId = lectureRes.data && lectureRes.data.id ? lectureRes.data.id : null
            }
            const notifRes = await lecturesService.createNotification(payload)
            const notifId = notifRes.data && notifRes.data.notification && notifRes.data.notification.id ? notifRes.data.notification.id : null
            const targetLectureId = lectureId || this.state.pendingLectureId
            if (targetLectureId && notifId) {
                await lecturesService.update(targetLectureId, { notification_ids: [notifId] })
            }
            message.success('ההתראה נשלחה')
            this.setState({ notifModalOpen: false, pendingLecture: null, pendingLectureId: null })
            this.load(this.state.page)
        } catch (e) {
            message.error('שגיאה בשליחת ההתראה')
        } finally {
            this.setState({ notifSaving: false })
        }
    }

    columns = () => [
        {
            title: 'נושא',
            dataIndex: 'topic',
            width: '18%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell
        },
        {
            title: 'תיאור',
            dataIndex: 'description',
            width: '13%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => renderTruncatedValue(v)
        },
        {
            title: 'תאריך ושעה',
            dataIndex: 'lecture_at',
            width: '13%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => moment(v).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'כתובת',
            dataIndex: 'lecture_address',
            width: '16%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (v: string) => v || '—'
        },
        {
            title: 'קישור הרשמה',
            key: 'registration',
            width: '10%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (_: any, row: any) => renderLinkButton(row.registration_link, 'הרשמה')
        },
        {
            title: 'קישור דיגיטלי',
            key: 'link',
            width: '10%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (_: any, row: any) => renderLinkButton(row.digital_link, 'קישור')
        },
        {
            title: '',
            key: 'action',
            width: '10%',
            onCell: () => ({ style: { verticalAlign: 'middle' as const } }),
            render: (_: any, row: any) => {
                const isFuture = new Date(row.lecture_at) > new Date()
                return renderActionButtons({
                    editLabel: isFuture ? 'עריכה' : 'צפייה',
                    onEdit: () => this.openEdit(row),
                    onDelete: () => this.deleteLecture(row.id),
                    disabled: !isFuture
                })
            }
        }
    ]

    render() {
        const { data, total, page, loading, modalOpen, selected, notifModalOpen, notifSaving } = this.state
        const futureCount = (data as any[]).filter((r: any) => new Date(r.lecture_at) > new Date()).length
        const subtitle = data.length === 0 ? '' : futureCount === 1 ? 'הרצאה אחת עתידית' : futureCount + ' הרצאות עתידיות'
        return (
            <Layout style={rtlLayoutStyle}>
                <MediwhoAppPageHeader
                    title="הרצאות"
                    subtitle={subtitle}
                    actionLabel="+ הרצאה חדשה"
                    onAction={this.openAdd}
                />
                <div style={mediwhoAppTableWrapStyle} className="mediwho-app-table-wrap">
                    <Table
                        rowKey="id"
                        loading={loading}
                        dataSource={data}
                        columns={this.columns()}
                        tableLayout="fixed"
                        rowClassName={(row: any) =>
                            new Date(row.lecture_at) < new Date() ? 'mediwho-app-row-past' : ''
                        }
                        pagination={{
                            current: page,
                            total,
                            pageSize: 10,
                            onChange: (p: number) => this.load(p)
                        }}
                    />
                </div>
                <LectureModal visible={modalOpen} lecture={selected} onClose={this.closeModal} onSaved={this.onSaved} />
                <NotificationModal
                    visible={notifModalOpen}
                    saving={notifSaving}
                    notification={this.state.notifPrefill}
                    onCancel={() => this.setState({ notifModalOpen: false })}
                    onSubmit={this.handleNotifSubmit}
                />
            </Layout>
        )
    }
}
